import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { businessInfo } from "../data/mock";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO 
        title="Privacy Policy - Trademark Brand Logo"
        description="Privacy Policy for Trademark Brand Logo. Learn how we collect, use, and protect your personal information when you use our trademark registration services."
        keywords="privacy policy, data protection, trademark services privacy, personal information security"
      />
      <Header onOpenForm={() => {}} />
      
      <main className="pt-24 pb-20">
        <div className="container-custom px-4 max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center text-[#1e3a8a] hover:text-[#d4af37] transition-colors duration-300 font-medium"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </div>

          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg border border-gray-100 mb-8">
              <p className="text-gray-700 leading-relaxed">
                At {businessInfo.name}, we are committed to protecting your privacy and ensuring the security 
                of your personal information. This Privacy Policy explains how we collect, use, disclose, and 
                safeguard your information when you visit our website and use our services.
              </p>
            </div>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">1. Information We Collect</h2>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                We collect personal information that you voluntarily provide to us when you register for our services, 
                express an interest in obtaining information about us or our products and services, or otherwise contact us. 
                This information may include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                <li>Name and contact information (email address, phone number, mailing address)</li>
                <li>Business information (company name, business type, industry)</li>
                <li>Trademark information (proposed marks, descriptions, classifications)</li>
                <li>Payment information (credit card details, billing address)</li>
                <li>Communication preferences and feedback</li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">Automatically Collected Information</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                When you visit our website, we automatically collect certain information about your device and browsing 
                activities, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                <li>IP address and browser type</li>
                <li>Operating system and device information</li>
                <li>Pages visited, time spent on pages, and navigation patterns</li>
                <li>Referring website addresses</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">2. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                We use the information we collect for various purposes, including:
              </p>
              <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
                <li>
                  <strong>Providing Services:</strong> To process your trademark applications, conduct searches, 
                  file documents with the USPTO, and provide related legal services
                </li>
                <li>
                  <strong>Communication:</strong> To respond to your inquiries, send service updates, and provide 
                  customer support
                </li>
                <li>
                  <strong>Account Management:</strong> To create and maintain your account, process payments, 
                  and manage your service requests
                </li>
                <li>
                  <strong>Improvement:</strong> To analyze usage patterns, improve our website functionality, 
                  and enhance our service offerings
                </li>
                <li>
                  <strong>Marketing:</strong> To send promotional materials, newsletters, and information about 
                  our services (with your consent)
                </li>
                <li>
                  <strong>Legal Compliance:</strong> To comply with legal obligations, protect our rights, 
                  and prevent fraud or misuse of our services
                </li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">3. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                We do not sell, trade, or rent your personal information to third parties. We may share your 
                information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
                <li>
                  <strong>Government Agencies:</strong> With the USPTO and other relevant government agencies 
                  as necessary to process your trademark applications
                </li>
                <li>
                  <strong>Service Providers:</strong> With trusted third-party service providers who assist us 
                  in operating our website, conducting business, or servicing you (subject to confidentiality agreements)
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law, court order, or governmental request, 
                  or when necessary to protect our rights or the rights of others
                </li>
                <li>
                  <strong>Business Transfers:</strong> In connection with any merger, sale of company assets, 
                  financing, or acquisition of all or a portion of our business
                </li>
                <li>
                  <strong>With Your Consent:</strong> When you have given us explicit permission to share your 
                  information for a specific purpose
                </li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">4. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                We implement appropriate technical and organizational security measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                <li>Encryption of sensitive data during transmission (SSL/TLS)</li>
                <li>Secure storage of personal information on protected servers</li>
                <li>Regular security assessments and updates</li>
                <li>Limited access to personal information by authorized personnel only</li>
                <li>Employee training on data protection and security practices</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                However, no method of transmission over the Internet or electronic storage is 100% secure. While we 
                strive to use commercially acceptable means to protect your personal information, we cannot guarantee 
                its absolute security.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">5. Your Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-6">
                <li>
                  <strong>Access:</strong> Request access to the personal information we hold about you
                </li>
                <li>
                  <strong>Correction:</strong> Request correction of inaccurate or incomplete information
                </li>
                <li>
                  <strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)
                </li>
                <li>
                  <strong>Opt-Out:</strong> Opt out of marketing communications at any time
                </li>
                <li>
                  <strong>Data Portability:</strong> Request a copy of your data in a structured, machine-readable format
                </li>
                <li>
                  <strong>Objection:</strong> Object to our processing of your personal information in certain circumstances
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                To exercise any of these rights, please contact us at {businessInfo.email} or {businessInfo.phone}.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">6. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                We use cookies and similar tracking technologies to enhance your browsing experience, analyze site 
                traffic, and understand user preferences. You can control cookie settings through your browser preferences. 
                However, disabling cookies may limit your ability to use certain features of our website.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">7. Third-Party Links</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices 
                or content of these external sites. We encourage you to review the privacy policies of any third-party 
                sites you visit.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">8. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal 
                information from children. If you believe we have collected information from a child, please contact us 
                immediately so we can take appropriate action.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">9. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal 
                requirements. We will notify you of any material changes by posting the updated policy on our website 
                and updating the "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">10. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, 
                please contact us:
              </p>
              <div className="bg-gradient-primary text-white p-8 rounded-xl">
                <p className="mb-2"><strong>{businessInfo.name}</strong></p>
                <p className="mb-2">{businessInfo.address}</p>
                <p className="mb-2">Email: {businessInfo.email}</p>
                <p>Phone: {businessInfo.phone}</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
